import { Link } from "react-router-dom";
import { ArrowRight, Zap, Wrench, Building, Paintbrush, Home, Building2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface UseCase {
  slug: string;
  title: string;
  description: string;
  icon: LucideIcon;
  color: string;
}

const allUseCases: UseCase[] = [
  {
    slug: "electricien",
    title: "Electricien",
    description: "Gerez vos chantiers et tickets de caisse",
    icon: Zap,
    color: "#F59E0B",
  },
  {
    slug: "plombier",
    title: "Plombier-Chauffagiste",
    description: "Pilotez vos equipes et factures",
    icon: Wrench,
    color: "#3B82F6",
  },
  {
    slug: "macon",
    title: "Macon / Entreprise generale",
    description: "Maitrisez vos couts de main d'oeuvre",
    icon: Building,
    color: "#F97316",
  },
  {
    slug: "peintre",
    title: "Peintre",
    description: "Optimisez vos marges au m2",
    icon: Paintbrush,
    color: "#8B5CF6",
  },
  {
    slug: "couvreur",
    title: "Couvreur-Charpentier",
    description: "Anticipez l'impact des intemperies",
    icon: Home,
    color: "#10B981",
  },
  {
    slug: "multi-corps",
    title: "Entreprise tous corps d'etat",
    description: "Vision consolidee multi-projets",
    icon: Building2,
    color: "#D4AF37",
  },
];

interface RelatedUseCasesProps {
  currentSlug: string;
}

const RelatedUseCases = ({ currentSlug }: RelatedUseCasesProps) => {
  // Filtrer le cas d'usage actuel et prendre 3 autres
  const relatedCases = allUseCases
    .filter((uc) => uc.slug !== currentSlug)
    .slice(0, 3);

  return (
    <section className="relative py-20 lg:py-24 bg-[#0A0A0F] border-t border-white/[0.05]">
      <div className="container relative z-10 mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section header */}
          <div className="mb-10 animate-fade-up">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Decouvrez aussi...
            </h2>
            <p className="text-white/50">
              D'autres professionnels du BTP utilisent B8ild
            </p>
          </div>

          {/* Related cards */}
          <div className="grid gap-4 sm:grid-cols-3">
            {relatedCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <Link
                  key={useCase.slug}
                  to={`/use-cases/${useCase.slug}`}
                  className="group relative rounded-xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent animate-fade-up hover:from-white/[0.2] transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="rounded-xl bg-[#0F0F14]/90 backdrop-blur-xl p-5 h-full">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: `${useCase.color}20` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: useCase.color }} />
                    </div>
                    <h3 className="text-white font-semibold mb-1 group-hover:text-[#F59E0B] transition-colors">
                      {useCase.title}
                    </h3>
                    <p className="text-white/40 text-sm mb-3">
                      {useCase.description}
                    </p>
                    <div className="flex items-center gap-1 text-sm text-white/50 group-hover:text-[#F59E0B] transition-colors">
                      Voir le cas d'usage
                      <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Link to all use cases */}
          <div className="mt-8 text-center animate-fade-up [animation-delay:300ms]">
            <Link
              to="/use-cases"
              className="inline-flex items-center gap-2 text-white/60 hover:text-[#F59E0B] transition-colors"
            >
              Voir tous les cas d'usage
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RelatedUseCases;
